"use client";

import Image from "next/image";
import { motion } from "framer-motion";

type Item = {
  index: string; // "01", "02"...
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
};

/**
 * Showcase en zigzag : chaque item occupe une ligne complète avec image + texte,
 * et l'orientation alterne (image-gauche / image-droite) pour donner un rythme
 * de portfolio éditorial. Plus narratif qu'un grid de cards.
 */
export default function AlternatingShowcase({
  eyebrow,
  title,
  items,
}: {
  eyebrow: string;
  title: string;
  items: Item[];
}) {
  return (
    <section className="bg-[#0b0b0d] py-24 md:py-32">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-20 md:mb-28">
          <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-body">
            {eyebrow}
          </p>
          <h2 className="mt-6 font-heading text-4xl md:text-5xl leading-[1.1] font-light text-white">
            {title}
          </h2>
        </div>

        {/* Zigzag items */}
        <div className="space-y-24 md:space-y-32">
          {items.map((item, i) => {
            const reverse = i % 2 === 1;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-120px" }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className={[
                  "grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-16 md:items-center",
                  reverse ? "md:[&>*:first-child]:order-2" : "",
                ].join(" ")}
              >
                {/* Image */}
                <div className="relative aspect-[4/5] md:aspect-[5/6] w-full overflow-hidden rounded-sm">
                  <Image
                    src={item.imageSrc}
                    alt={item.imageAlt}
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                </div>

                {/* Texte */}
                <div className={reverse ? "md:pr-6" : "md:pl-6"}>
                  <span className="font-heading text-7xl md:text-8xl italic font-light text-white/15 leading-none select-none block">
                    {item.index}
                  </span>
                  <h3 className="mt-5 font-heading text-3xl md:text-4xl lg:text-5xl font-light text-white leading-[1.1]">
                    {item.title}
                  </h3>
                  <p className="mt-6 text-base md:text-lg leading-relaxed text-white/65 font-body font-light max-w-lg">
                    {item.description}
                  </p>
                  <span className="mt-8 block h-px w-16 bg-white/25" />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
